import type { ComponentPropsWithoutRef } from 'react';
import { css, cx } from '@linaria/atomic';
import { Icon } from '@frontend/icons';
import { Chip, type ChipProps } from '@frontend/design-system';
import { useCareCreditTranslations } from '../hooks/use-care-credit-translations';
import type { CareCreditChipStatus } from '../types/care-credit.types';

const styles = {
  chip: css`
    font-size: var(--fabric-typography-body-xs-font-size);
    gap: var(--fabric-space-gap-xxs);
    user-select: none;
  `,
  unknown: css`
    color: var(--fabric-color-neutral-60);
  `,
};

export type CareCreditChipProps = {
  containerRef?: ChipProps['containerRef'];
  isResponsive?: ChipProps['isResponsive'];
  isIconMode?: boolean;
  size?: ChipProps['size'];
  status?: CareCreditChipStatus;
  maxWidth?: ChipProps['maxWidth'];
} & Omit<ComponentPropsWithoutRef<'div'>, 'children' | 'onChange'>;

export const CareCreditChip = ({
  className,
  containerRef,
  isResponsive,
  size = 'default',
  status,
  isIconMode,
  ...props
}: CareCreditChipProps) => {
  const { t } = useCareCreditTranslations();
  const chipClassName = cx(styles.chip, className);
  const chipIconSize = size === 'tiny' ? 12 : 14;
  const sharedChipProps = {
    ...props,
    className: chipClassName,
    containerRef,
    hideTooltip: true,
    isResponsive,
    size,
  };

  function getChipProps(): {
    leftElement: React.ReactNode;
    variant: ChipProps['variant'];
    text: React.ReactNode;
    className?: string;
  } | null {
    if (status === 'CARDHOLDER') {
      return {
        leftElement: <Icon name='care-credit-cardholder' size={chipIconSize} />,
        variant: 'success',
        text: t('Cardholder'),
      };
    }

    if (status === 'PREAPPROVED') {
      return {
        leftElement: <Icon name='care-credit-preapproved' size={chipIconSize} />,
        variant: 'seaweed',
        text: t('Preapproved'),
      };
    }

    if (status === 'PREQUALIFICATION_NEEDED') {
      return {
        leftElement: <Icon name='care-credit-prequalification-check' size={chipIconSize} />,
        variant: 'warn',
        text: t('Prequalification check'),
      };
    }

    if (status === 'UNKNOWN') {
      return {
        leftElement: <Icon name='help' size={chipIconSize} />,
        variant: 'neutral',
        text: t('Status unknown'),
        className: cx(sharedChipProps.className, styles.unknown),
      };
    }

    if (status === 'ERROR') {
      return {
        leftElement: <Icon name='x' size={chipIconSize} />,
        variant: 'critical',
        text: t('Error'),
      };
    }

    return null;
  }

  const chipProps = getChipProps();

  if (chipProps) {
    if (isIconMode) {
      return (
        <Chip {...sharedChipProps} variant={chipProps.variant}>
          <div css={{ width: chipIconSize }}>{chipProps.leftElement}</div>
        </Chip>
      );
    }
    return (
      <Chip {...sharedChipProps} leftElement={chipProps.leftElement} variant={chipProps.variant}>
        {chipProps.text}
      </Chip>
    );
  }

  return null;
};
